import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { motion } from 'framer-motion';
import NewsletterSignup from '@/components/NewsletterSignup';

const Footer = () => {
  const { t, isReady: isLanguageReady } = useLanguage();
  const { settings, isLoading: isLoadingSiteSettings } = useSiteSettings();
  const currentYear = new Date().getFullYear();

  if (!isLanguageReady || isLoadingSiteSettings) {
    return (
      <footer className="border-t animate-pulse bg-background border-border/60 text-muted-foreground">
        <div className="container px-4 py-12 mx-auto">
          <div className="grid grid-cols-2 gap-8 mb-8 md:grid-cols-4 lg:grid-cols-5">
            <div className="col-span-2 mb-6 lg:col-span-1 lg:mb-0">
              <div className="mb-3 w-32 h-8 rounded bg-muted"></div>
              <div className="w-48 h-4 rounded bg-muted"></div>
            </div>
            {[...Array(4)].map((_, i) => (
              <div key={i}>
                <div className="mb-4 w-24 h-5 rounded bg-muted"></div>
                <ul className="space-y-3">
                  {[...Array(3)].map((_, j) => (
                    <li key={j}><div className="w-32 h-4 rounded bg-muted"></div></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="flex flex-col justify-between items-center pt-8 border-t border-border/60 md:flex-row">
            <div className="w-56 h-4 rounded bg-muted"></div>
            <div className="flex gap-4 mt-4 md:mt-0">
              <div className="w-16 h-4 rounded bg-muted"></div>
              <div className="w-20 h-4 rounded bg-muted"></div>
            </div>
          </div>
          <div className="pt-8 mt-8 border-t border-border/60">
            <div className="mb-2 w-full h-4 rounded bg-muted"></div>
            <div className="w-3/4 h-4 rounded bg-muted"></div>
          </div>
        </div>
      </footer>
    );
  }
  
  const footerSections = [
    {
      key: 'company',
      titleKey: 'footer.company',
      links: [
        { labelKey: 'footer.about', path: '/about' },
        { labelKey: 'footer.features', path: '/features' },
        { labelKey: 'footer.whyUrbanMint', path: '/why-urbanmint' },
        { labelKey: 'footer.sponsors', path: '/sponsors' },
        { labelKey: 'footer.contact', path: '/contact' },
      ],
    },
    {
      key: 'resources',
      titleKey: 'footer.resources',
      links: [
        { labelKey: 'nav.learnHub', path: '/learn' },
        { labelKey: 'nav.mentors', path: '/mentors' },
        { labelKey: 'nav.freeResources', path: '/free-resources' },
        { labelKey: 'nav.glossary', path: '/glossary' },
        { labelKey: 'nav.scenarios', path: '/scenarios' },
      ],
    },
    {
      key: 'support',
      titleKey: 'footer.support',
      links: [
        { labelKey: 'footer.helpRequest', path: '/help-request' },
        { labelKey: 'footer.support', path: '/support' },
      ],
    },
    {
      key: 'nonprofit',
      titleKey: 'footer.nonprofit',
      links: [],
    },
  ];

  const siteTitle = settings?.site_title || t('site.title');
  const siteTagline = t('site.tagline');
  const footerDisclaimer = settings?.footer_disclaimer || t('footer.disclaimer');


  return (
    <motion.footer 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="border-t bg-background border-border/60 text-muted-foreground"
    >
      <div className="container px-4 py-12 mx-auto">
        <div className="grid grid-cols-2 gap-8 mb-8 md:grid-cols-4 lg:grid-cols-5">
          <div className="col-span-2 mb-6 lg:col-span-1 lg:mb-0">
            <Link to="/" className="flex flex-col items-start">
              <img 
                src="/assets/logo_Main.png" 
                alt="UrbanMint logo" 
                className="mb-3 w-auto h-24"
              />
              <p className="text-sm text-muted-foreground">{siteTagline}</p>
            </Link>
          </div>

          {footerSections.map((section) => (
            <div key={section.key}>
              <h3 className="mb-4 text-sm font-semibold tracking-wider uppercase text-foreground">
                {t(section.titleKey)}
              </h3>
              {section.key === 'nonprofit' ? (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {t('footer.nonprofitDescription')}
                  </p>
                </div>
              ) : (
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link.labelKey}>
                      <Link
                        to={link.path}
                        className="text-sm underline transition-colors duration-200 hover:text-primary"
                      >
                        {t(link.labelKey)}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
        
        {/* Newsletter */}
        <div className="pt-8 mt-8 border-t border-border/60">
          <NewsletterSignup />
        </div>

        {/* Disclaimer */}
        <div className="pt-8 mt-8 border-t border-border/60">
          <p className="text-sm text-muted-foreground">
            {footerDisclaimer}
          </p>
        </div>

        {/* Copyright and Legal Links */}
        <div className="flex flex-col justify-between items-center pt-8 border-t border-border/60 md:flex-row">
          <p className="text-sm text-center md:text-left">
            {t('footer.copyright', { year: currentYear })}{' '}
            <a
              href="https://www.responsab.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-primary"
            >
              {t('footer.responsabProject')}
            </a>
          </p>

          <div className="flex gap-4 mt-4 md:mt-0">
            <Link
              to="/privacy"
              className="text-sm underline transition-colors duration-200 hover:text-primary"
            >
              {t('footer.privacy')}
            </Link>
            <Link
              to="/terms"
              className="text-sm underline transition-colors duration-200 hover:text-primary"
            >
              {t('footer.terms')}
            </Link>
          </div>
        </div>
      </div>
    </motion.footer>
  );
};

export default Footer;
  
